import React from 'react'
import Img from '../../../Img'
import styles from './styles.module.css'
import SimpleBlockContent from '../../../SimpleBlockContent'
import Text from '../../../Text'
import VRSLogo from '../../../icons/VRSLogo'
import Youtube from '../../../Youtube'
import { formatDate } from '../../LegalDocumentPage'

const SITE_URL = 'https://vrs.racing'
const PUBLISHER_LOGO = 'https://storage.googleapis.com/vrs-media/assets/images/967b25b71e1af897434befcaef4c6be9ce91a94e-62x22.svg'

// App-Router-safe JSON-LD builders. next-seo's <ArticleJsonLd>/<FAQPageJsonLd>
// render through next/head, whose useContext(HeadManagerContext) returns null
// under App Router SSR — they THROW, bailing the nearest next/dynamic Suspense
// boundary (React #419) and dropping the structured data from the SSR HTML. We
// emit the SAME schema.org objects next-seo would build as raw
// <script type="application/ld+json"> nodes (task #119), mirroring the #116 fix
// in components/sections/FaqPage/index.js and the raw emitter in
// app/[[...slug]]/RegionPageClient.jsx.
function buildArticleJsonLd({ url, title, images, datePublished, dateModified, description }) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    datePublished,
    description,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': url,
    },
    headline: title,
    image: images,
    dateModified: dateModified || datePublished,
    // "VRS Racing" is the brand ORGANIZATION, not a person — reference the
    // site-wide #organization node (defined by SiteJsonLd in app/layout.js) so the
    // author resolves to the same entity (audit C-H2 #4).
    author: {
      '@type': 'Organization',
      '@id': `${SITE_URL}/#organization`,
      name: 'Virtual Racing School',
      url: SITE_URL,
    },
    publisher: {
      '@type': 'Organization',
      name: 'VRS Racing',
      logo: { '@type': 'ImageObject', url: PUBLISHER_LOGO },
    },
  }
}

function buildFaqJsonLd(items) {
  if (!items || !items.length) return null
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map(q => ({
      '@type': 'Question',
      name: q.questionName,
      acceptedAnswer: {
        '@type': 'Answer',
        text: q.acceptedAnswerText,
      },
    })),
  }
}

function BlogPostPage({ blogPost }) {
  // thumbnail is a pre-resolved ResolvedImage descriptor (task #67 walker).
  const thumb = blogPost.thumbnail && blogPost.thumbnail.src ? blogPost.thumbnail : null
  const articleUrl = blogPost.slug ? `${SITE_URL}/${blogPost.slug}` : ''

  const articleJsonLd = buildArticleJsonLd({
    url: articleUrl,
    title: blogPost.title,
    images: thumb ? [thumb.src] : [],
    datePublished: blogPost.date,
    dateModified: blogPost.date,
    description: blogPost.description || blogPost.title,
  })

  const faqJsonLdItems = blogPost.faqs?.length > 0
    ? blogPost.faqs.map(faq => ({
        questionName: faq.question,
        acceptedAnswerText: faq.answer,
      }))
    : []
  const faqJsonLd = buildFaqJsonLd(faqJsonLdItems)

  return (
    <>
      <script
        type="application/ld+json"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }}
      />
      {faqJsonLd && (
        <script
          type="application/ld+json"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }}
        />
      )}
      <div className={styles.wrapper}>
        <div className={styles.contentWrapper}>
          <div className={styles.postTitle}>
            <Text type="heading-1" primary>{blogPost.title}</Text>
            <div className={styles.postDetails}>
              <div className={styles.logoWrapper}><VRSLogo/></div>
              <div className={styles.publishingInfo}>
                <div className={styles.authorName}><Text type="medium">VRS® Racing</Text></div>
                <div className={styles.datePublished}>
                  {blogPost.date && <Text type="small">{formatDate(new Date(blogPost.date))}</Text>}
                  {blogPost.date && blogPost.minutesLong && <Text type="small">{'  |  '}</Text>}
                  {blogPost.minutesLong && <Text type="small">{blogPost.minutesLong} mins read</Text>}
                </div>
              </div>
            </div>
            {blogPost.videoUrl ? (
            <div className={styles.blogPostVideo}>
              <Youtube
                url={blogPost.videoUrl}
                name={blogPost.title}
                description={blogPost.description || blogPost.title}
                uploadDate={blogPost.date}
              />
            </div>
          ) : (
            <div className={styles.blogPostImage}>
              {/* LCP of a blog post: canonical <Img> eager + fetchPriority so the
                  largest paint starts from the SSR HTML (task #67). */}
              {thumb && (
                <Img
                  image={thumb}
                  alt={blogPost.title}
                  layout="responsive"
                  sizes="(max-width: 768px) 100vw, (max-width: 1200px) 80vw, 1200px"
                  priority
                  objectFit="cover"
                />
              )}
            </div>
          )}
          </div>
          <SimpleBlockContent blocks={blogPost.text} applySpace/>
        </div>
      </div>
    </>
  )
}

export default BlogPostPage
